import { ResourceService } from './../../services/resource/resource.service';
import { Injectable } from '@angular/core';
import { Endpoints } from '../../../config/endpoints.config';
import { DiskService } from '../../services/disk/disk.service';
import { HttpClient } from '@angular/common/http';
import { LogService } from '../../services/logging/log-service';
import { IResource, Resource, OfflineResource } from '../../classes/resource/resource.class';
import { AuthenticationService } from '../../services/authentication.service';
import { EntitySyncInfo, EntityNames, DeltaService } from '../delta/delta.service';
import { FeatureActionsMap } from '../../classes/authentication/user.class';
import { DB_SYNC_STATE_KEYS, DB_KEY_PREFIXES, DB_ALLDOCS_QUERY_OPTIONS } from '../../config/pouch-db.config';
import { TrackAction } from '../../utility/common-enums';
import { Store } from '@ngrx/store';
import { ResourceState } from '../../store/application.state';
import * as ResourceAction from '../../store/io-file-service/actions/resource.actions';
import { NotificationService, ToastStyle } from '../../services/notification/notification.service';
import {MyAssistantService, NOTIFICATION} from '../../services/my-assistant/my-assistant.service';
import { TranslateService } from '@ngx-translate/core';
import { IoFileService } from '../../services/io-file-service/io-file-service';
import { SyncFeatureCategory } from '../../enums/delta-service/delta-service.enum';
import {IndNotificationDataModel} from "@omni/models/indNotificationDataModel";
import { EntityTag, TagEntityType, TagStateCode, UserTag, UserTagService } from '@omni/services/user-tag/user-tag.service';
import { fetchQueries } from '@omni/config/dynamics-fetchQueries';
import { CONTACT_FETCH_QUERIES } from '@omni/config/fetch-xml/contact-fetchXMLs';
import { DynamicsClientService } from '../dynamics-client/dynamics-client.service';
import { DeviceService } from '@omni/services/device/device.service';
import { isEmpty, uniqBy } from 'lodash-es';
import { OfflineServiceBase } from '../../services/offline/offline.service';
import { SyncProfilerService } from '../../services/logging/sync-profiler.service';
import { ResourceProfiler } from '../../services/logging/resource-profiler.service';



/**
 * Data service for Presentation
 *
 * @export
 * @class PresentationDataService
 */
@Injectable({
  providedIn: 'root'
})
export class ResourceDataService {

    private _isInitialMappingDone: boolean = false;
    private resourcesNotificationModel: IndNotificationDataModel;
    constructor(
        private disk: DiskService,
        private http: HttpClient,
        private resourceService: ResourceService,
        private logService: LogService,
        private authenticationService: AuthenticationService,
        private deltaService: DeltaService,
        private store: Store<ResourceState>,
        private assistantService: MyAssistantService,
        private notificationService: NotificationService,
        public translate: TranslateService,
        private readonly userTagService: UserTagService,
        private ioFileService: IoFileService,
        private dynamicsService: DynamicsClientService,
        private deviceService: DeviceService,
        private authService: AuthenticationService,
        private readonly syncProfiler: SyncProfilerService,
        private readonly resourceProfiler: ResourceProfiler,
    ) {

    }

    /**
     * Loads all per-doc resources from disk via allDocs range query.
     * Used for full sync and offline load only — never during delta sync.
     */
    private async loadResourcesFromDisk(): Promise<any[]> {
        const docs: any[] = await this.disk.batchFetch(DB_ALLDOCS_QUERY_OPTIONS.GET_ALL_RESOURCE_DOCS) as any[];
        if (Array.isArray(docs)) {
            return docs.map(doc => OfflineServiceBase.unsanitizeFromPouchDB(doc));
        }
        return [];
    }

    /**
     * Fetches _rev values for a set of doc IDs without loading full documents.
     * Used before upsert/delete to avoid 409 conflicts.
     */
    private async fetchRevs(docIds: string[]): Promise<Map<string, string>> {
        const revMap = new Map<string, string>();
        if (docIds.length === 0) return revMap;
        const result: any = await this.disk.batchFetch({ keys: docIds, include_docs: false }, false);
        if (result && Array.isArray(result.rows)) {
            for (const row of result.rows) {
                if (row.id && row.value && row.value.rev) {
                    revMap.set(row.id, row.value.rev);
                }
            }
        }
        return revMap;
    }

    /**
     * Chunked bulk write with underscore sanitization.
     * Fetches _rev only for docs that don't already carry it.
     */
    private async saveResourcesToDisk(resources: any[]): Promise<void> {
        if (resources.length === 0) return;
        const docs = resources.map(resource => {
            const id = resource.indskr_ioresourceid || resource.indskr_iodocumentid;
            return OfflineServiceBase.sanitizeForPouchDB({
                _id: `${DB_KEY_PREFIXES.RESOURCE}_${id}`,
                ...resource,
            });
        });
        // Only fetch revs for docs missing _rev (new docs from server response)
        const docsWithoutRev = docs.filter(d => !d._rev);
        if (docsWithoutRev.length > 0) {
            const docIds = docsWithoutRev.map(d => d._id);
            const revMap = await this.fetchRevs(docIds);
            for (const doc of docsWithoutRev) {
                const rev = revMap.get(doc._id);
                if (rev) {
                    doc._rev = rev;
                }
            }
        }
        await this.disk.bulkInChunks(docs);
    }

    /**
     * Bulk soft-delete resources by ID.
     * Fetches _rev first, then marks docs as _deleted.
     */
    private async bulkDeleteResourcesFromDisk(resourceIds: string[]): Promise<void> {
        if (resourceIds.length === 0) return;
        const docIds = resourceIds.map(id => `${DB_KEY_PREFIXES.RESOURCE}_${id}`);
        const revMap = await this.fetchRevs(docIds);
        const deleteDocs = [];
        for (const docId of docIds) {
            const rev = revMap.get(docId);
            if (rev) {
                deleteDocs.push({ _id: docId, _rev: rev, _deleted: true });
            }
        }
        if (deleteDocs.length > 0) {
            await this.disk.bulkInChunks(deleteDocs);
        }
    }

    /**
     * One-time migration: converts old single-doc `resources` to per-doc `resources_<id>`.
     * Idempotent — safe on retry.
     */
    public async migrateResourcesToDB(): Promise<void> {
        try {
            const oldDoc: any = await this.disk.retrieve(DB_KEY_PREFIXES.RESOURCE);
            if (!oldDoc || !oldDoc.raw || !Array.isArray(oldDoc.raw)) return;
            await this.saveResourcesToDisk(oldDoc.raw);
            await this.disk.remove(DB_KEY_PREFIXES.RESOURCE);
            console.log(`migrateResourcesToDB: migrated ${oldDoc.raw.length} resources to per-doc`);
        } catch (error) {
            console.error('migrateResourcesToDB: migration error', error);
        }
    }

    /**
     * Returns an array of presentation from either our disk or network depending on cache expiry time
     *
     * @memberof PresentationDataService
     */
    async getResources(loadFromDbOnly = false, profilerParentSpanId?: string) {
        if (!this.authenticationService.hasFeatureAction(FeatureActionsMap.RESOURCE_TOOL)) {
            this.resourceService.clearAllResources();
            return;
        }
        this.deltaService.pushSyncEntityName(SyncFeatureCategory.files);
        if (loadFromDbOnly) {
            await this.syncProfiler.timeAsync('resources_db_load',
                () => this.loadOfflineResources(), profilerParentSpanId);
        } else {
        let syncState = await this.disk.getSyncState(DB_SYNC_STATE_KEYS.SYNC_RESOURCE);
        const isDeltaSync = syncState && syncState.lastUpdatedTime && !this.authenticationService.shouldContentFullSync;

        let url: string = this.authenticationService.userConfig.activeInstance.entryPointUrl + Endpoints.resources.GET_RESOURCES;
        let params = {
            positionIds: this.authenticationService.user.positions.map((o) => {
                return o.ID
            })
        }
        url = url + "?positionIds=" + params.positionIds;
        url = isDeltaSync ? url + '&lastUpdatedTime=' + syncState.lastUpdatedTime : url;

        const resourceSyncInfo: EntitySyncInfo = {
            entityName: EntityNames.resource,
            totalFailed: 0,
            totalSynced: 0,
            errors: [],
            syncStatus: true
        };

        let response: any;
        try {
            response = await this.syncProfiler.timeAsync('resources_api',
                () => this.http.get<IResource[]>(url, Endpoints.GLOBAL_SYNC_HEADER).toPromise(),
                profilerParentSpanId);
        } catch (error) {
            this.deltaService.addSyncErrorToEntitySyncInfo(resourceSyncInfo, url, error);
        }

        if (response) {
            if (!this._isInitialMappingDone) {
                try {
                    await this.syncProfiler.timeAsync('resources_migration',
                        () => this.migrateResourcesToDB(), profilerParentSpanId);

                    const offlineresource = await this.disk.retrieve('offlineresource', true);
                    if (offlineresource) {
                        this.mapOfflineResources(offlineresource.offlineresource);
                    } else {
                        this.resourceService.offlineResources = [];
                    }
                    await this.syncProfiler.timeAsync('resources_initial_db_read', async () => {
                        const resources = await this.loadResourcesFromDisk();
                        if (resources.length > 0) {
                            this.mapResources(resources, true);
                        }
                    }, profilerParentSpanId);
                    this._isInitialMappingDone = true;
                } catch (error) {
                    this.logService.logError('Error occured while fetching resources from offline db');
                }
            }
            const newLastUpdatedTime = new Date().getTime();
            if (isDeltaSync) {
                await this.syncProfiler.timeAsync('resources_delta_map',
                    () => this.mapDeltaSyncedResources(response), profilerParentSpanId);
            } else {
                await this.syncProfiler.timeAsync('resources_full_map',
                    async () => this.mapResources(response, false), profilerParentSpanId);
            }
            // Done sync. Update sync state.
            if (resourceSyncInfo.syncStatus) {
                syncState.lastUpdatedTime = newLastUpdatedTime;
                resourceSyncInfo.totalSynced = response.length;
                await this.disk.updateSyncState(syncState);
            }
            this.deltaService.addEntitySyncInfo(resourceSyncInfo);

            this.disk.retrieve(DB_KEY_PREFIXES.RESOURCE_RECENT_SEARCHES, true).then((doc)=>{
                if(doc && doc.raw){
                  this.resourceService.recentSearches = doc.raw
                }
                else {
                  this.resourceService.recentSearches = [];
                }
              })
        }
        }
    }

    private mapOfflineResources(raw: any) {
        if (Array.isArray(raw)) {
            this.resourceService.refreshOfflineResourceList(raw);
        } else {
            console.error('Unsupported data format for presentations!');
        }
    }

    public async mapDeltaSyncedResources(raw: IResource[]) {
        let notifResources: Resource[] = [];
        if (!raw || !Array.isArray(raw)) return;

        if (raw.length === 0) {
            // Display offline saved new resources which were not visited before
            const dbResources = await this.loadResourcesFromDisk();
            if (Array.isArray(dbResources)) {
                const newResources = dbResources.filter(savedRes => savedRes.isNew);
                if (newResources.length > 0) {
                    console.log('Not visited new resources', newResources);
                    newResources.forEach(nr => {
                        this.deltaService.deltaRecordsDTO.resources.push(nr['indskr_ioresourceid']);
                    });
                }
            }
            return;
        }

        const upsertDocs: any[] = [];
        const deleteIds: string[] = [];

        for (let i = 0; i < raw.length; i++) {
            let dtoidtobeused: string;
            let idtobeused: string;
            if (raw[i] && raw[i].indskr_ioresourceid) {
                dtoidtobeused = 'indskr_ioresourceid';
                idtobeused = 'ioResourceId';
            } else if (raw[i] && raw[i].indskr_iodocumentid) {
                dtoidtobeused = 'indskr_iodocumentid';
                idtobeused = 'ioDocumentId';
            } else {
                console.log('Unhandled resource id for' + raw[i]);
                continue;
            }
            const rawResource = raw[i];

            if (rawResource.track_action && rawResource.track_action == TrackAction.Deleted) {
                // Remove from in-memory array
                const idx = this.resourceService.allResources.findIndex(a => a[idtobeused] == rawResource[dtoidtobeused]);
                const offdbidx = this.resourceService.offlineResources.findIndex(a => a.ioResourceId === rawResource[dtoidtobeused]);
                if (idx >= 0) {
                    this.resourceService.allResources.splice(idx, 1);
                }
                if (offdbidx >= 0) {
                    await this.deleteDownloadedResource(rawResource[dtoidtobeused]);
                }
                // Collect for batch delete from disk
                deleteIds.push(rawResource[dtoidtobeused]);

            } else if (!isNaN(parseInt(rawResource.statecode))) {
                const idx = this.resourceService.allResources.findIndex(a => a[idtobeused] == rawResource[dtoidtobeused]);
                const offdbidx = this.resourceService.offlineResources.findIndex(a => a.ioResourceId === rawResource[dtoidtobeused]);

                if (idx >= 0) {
                    if (parseInt(rawResource.statuscode) == 1) {
                        // Active — update in-memory
                        const resource = new Resource(rawResource);
                        if (rawResource['isNew']) {
                            this.deltaService.deltaRecordsDTO.resources.push(rawResource[dtoidtobeused]);
                        }
                        if (offdbidx >= 0) {
                            resource.downloaded = true;
                        }
                        this.resourceService.allResources[idx] = resource;
                        this.resourceService.mapResourceFieldsToSearchIndex(resource);
                        // Collect for batch upsert
                        upsertDocs.push(rawResource);
                    } else if (parseInt(rawResource.statuscode) !== 1) {
                        // Inactive — remove from in-memory
                        if (offdbidx >= 0) {
                            await this.deleteDownloadedResource(rawResource[dtoidtobeused]);
                        }
                        this.resourceService.allResources.splice(idx, 1);
                        // Collect for batch delete
                        deleteIds.push(rawResource[dtoidtobeused]);
                    } else {
                        console.warn('mapDeltaSyncedResources: Invalid statuscode ' + parseInt(rawResource.statuscode), rawResource.indskr_ioresourceid);
                    }
                } else {
                    // New resource
                    if (parseInt(rawResource.statuscode) == 1) {
                        const resource = new Resource(rawResource);
                        this.resourceService.allResources.push(resource);
                        rawResource['isNew'] = false;
                        this.deltaService.deltaRecordsDTO.resources.push(rawResource[dtoidtobeused]);
                        notifResources.push(resource);
                        // Collect for batch upsert
                        upsertDocs.push(rawResource);
                    }
                    this.resourceService.mapResourceFieldsToSearchIndex(new Resource(rawResource));
                }
            }
        }

        // Batch upsert changed docs
        if (upsertDocs.length > 0) {
            await this.saveResourcesToDisk(upsertDocs);
        }

        // Batch delete removed docs
        if (deleteIds.length > 0) {
            await this.bulkDeleteResourcesFromDisk(deleteIds);
        }

        if (notifResources.length) {
            let displayName = notifResources.length == 1 ? this.translate.instant("RESOURCE_LOWER") : this.translate.instant("RESOURCES");
            let showCount = notifResources.length == 1 ? '' : notifResources.length;
            this.resourcesNotificationModel = {
                type: NOTIFICATION.NEW_RESOURCES_NOTIFICATION,
                name: this.translate.instant("NEW_RESOURCES_NOTIFICATION", {count: showCount, resource: displayName}),
                DateTime: Date.now(),
                id: NOTIFICATION.NEW_RESOURCES_NOTIFICATION + Date.now(),
                data: {
                    data: notifResources
                },
                icon: 'assets/imgs/resource_notification.svg',
                isRed: false,
                params: {count: showCount, resource: displayName}
            }
            this.assistantService.saveNotificationToDisk(this.resourcesNotificationModel);
        }
    }

    public async mapResources(raw: IResource[], doNotSave?: boolean) {
        if (Array.isArray(raw)) {
            this.resourceService.refreshResourceList(raw);
        } else {
            console.error('Unsupported data format for resources!');
            return;
        }

        if (!doNotSave) {
            try {
                await this.saveResourcesToDisk(raw);
                this.logService.logInfo("Successfully saved resources");
            } catch (error) {
                this.logService.logError(`Failed saving resource. Error: ${error}`);
            }
        }
    }

    public async loadOfflineResources() {
        console.log("Offline resources");
        const offlineresource = await this.disk.retrieve('offlineresource');
        if (offlineresource) {
            this.mapOfflineResources(offlineresource.offlineresource);
        } else {
            this.resourceService.offlineResources = [];
        }
        const resources = await this.loadResourcesFromDisk();
        if (resources.length > 0) {
            this.mapResources(resources, true);
        }
        this._isInitialMappingDone = true;
    }

    public saveResourceForOffline(resourceId: string, downloaded: boolean) {
        let resourceOffline: OfflineResource = null;
        let resource: Resource = this.resourceService.getResourceById(resourceId, resourceId);
        if (resource) {
            if (!downloaded) {
                console.log("Deleting ", resource.title);
                resource.downloaded = false;
                this.notificationService.notify(this.translate.instant('FAILED_TO_DOWNLOAD_RESOURCE') + resource.title, "io-file-service", "top", ToastStyle.DANGER, 3000, true);
                setTimeout(() => { this.store.dispatch(new ResourceAction.downloadResourceDequeue()) }, 500);
                return;
            }
            resourceOffline = {
                ioResourceId: resource.ioResourceId ? resource.ioResourceId : resource.ioDocumentId,
                name: resource.title,
                downloaded: downloaded,
                downloadDate: new Date()
            }
        }
        this.resourceService.offlineResources.push(resourceOffline);
        this.disk.retrieve('offlineresource').then(async (resources) => {
            let resourceArray = [];
            if (resources) {
                resources.offlineresource.push(resourceOffline);
                resourceArray = resources.offlineresource;
            } else {
                resourceArray = [resourceOffline];
            }
            this.disk.updateOrInsert('offlineresource', doc => ({ offlineresource: resourceArray }));
            this.store.dispatch(new ResourceAction.downloadResourceDequeue());
            resource.downloaded = true;

        }).catch(diskError => {
            this.store.dispatch(new ResourceAction.downloadResourceDequeue());
            console.error('Caught disk error trying to load resources from disk', diskError);
        });
        // this.assistantService.addNewNotification(resource.title + this.translate.instant('PRESENTATION_DOWNLODING_FINISHED'));
        this.notificationService.notify(resource.title + this.translate.instant('PRESENTATION_DOWNLODING_FINISHED'), "Resource Service")
        console.log('KB: resource download end', new Date());
    }

    public deleteDownloadedResource(resourceId: string) {

        let resource: Resource = this.resourceService.getResourceById(resourceId, resourceId);
        if (resource) resource.downloaded = false;
        const temp = this.resourceService.getOfflineResourceById(resourceId);
        if (temp) {
            const index = this.resourceService.offlineResources.indexOf(temp);
            this.resourceService.offlineResources.splice(index, 1);
        }
        try {
            this.ioFileService.deleteDownloadedResource(resourceId);
            this.disk.updateOrInsert('offlineresource', doc => ({ offlineresource: this.resourceService.offlineResources }))
        } catch (error) {
            console.log("Error while deleteing resource " + resource.title);
        }

    }
    async updateSyncStateResourceTag(newLastUpdatedTime: string) {
      const syncState = await this.disk.getSyncState(DB_SYNC_STATE_KEYS.SYNC_RESOURCE_TAG);
      const resourceTagsSyncInfo: EntitySyncInfo = {
        entityName: EntityNames.resource,
        totalFailed: 0,
        totalSynced: 0,
        errors: [],
        syncStatus: true
      };

      syncState.lastUpdatedTime = newLastUpdatedTime;
      await this.disk.updateSyncState(syncState);
      if (Array.isArray(this.userTagService.resourceTag)) {
        resourceTagsSyncInfo.totalSynced = this.userTagService.resourceTag.length;
      }
      this.deltaService.addEntitySyncInfo(resourceTagsSyncInfo);
    }
    
    public async loadResourceTagFromDB() {
      try {
        const data = await this.disk.batchFetch(DB_ALLDOCS_QUERY_OPTIONS.GET_ALL_USER_TAG);
        if (Array.isArray(data)) {
          this.userTagService.resourceTag = data?.filter(a => a.entity === TagEntityType.RESOURCE);
        }
      } catch (error) {
        console.error('Error loading resource tags from DB:', error);
      }
    }

    public async updateNewResourceStatus(newResources: Resource[]) {
        if (!newResources || newResources.length === 0) return;

        return this.resourceProfiler.timeAsync('update_new_resource_status', async () => {
            // Fetch only the specific docs by key — not the entire collection
            const docIds = newResources.map(res => {
                const id = res.ioResourceId || res.ioDocumentId;
                return `${DB_KEY_PREFIXES.RESOURCE}_${id}`;
            });
            const result: any = await this.disk.batchFetch({ keys: docIds, include_docs: true }, false);
            if (!result?.rows) return;

            const upsertDocs: any[] = [];
            for (const row of result.rows) {
                if (row.doc && !row.error) {
                    const doc = OfflineServiceBase.unsanitizeFromPouchDB(row.doc);
                    doc['isNew'] = false;
                    upsertDocs.push(doc);
                }
            }
            if (upsertDocs.length > 0) {
                await this.saveResourcesToDisk(upsertDocs);
            }
        }, { resourceCount: newResources.length });
    }

}
